<script lang="ts">
import { GuidesSchema } from '$lib/schema-org';
import GuideFooter from '../guide-footer.svelte';
import InlineCTA from '../InlineCTA.svelte';
import HeroImage from './hero.png';
import type { SponsorCopy } from '$lib/sponsor';

const sponsorCopy: SponsorCopy = {
  intro: "A career split across countries is exactly the case this calculator can't finish. You can hand the details to a Social Security specialist at",
  outro: "and get an estimate that counts your overseas work.",
  bullets: [
    "A totalization claim runs through two systems and needs records from both, and mistakes are slow to unwind.",
    "They also cover what this guide doesn't, like taxes, pensions, and health.",
    "The first call is free, and you pick the time.",
  ],
};

const title = 'Social Security International Agreements';
const description = 'How does working abroad affect social security?';
const publishDate = new Date('2022-06-26T00:00:00+00:00');

let schema: GuidesSchema = new GuidesSchema();
schema.url = 'https://ssa.tools/guides/international-agreements';
schema.title = title;
schema.image = HeroImage; // Added HeroImage to schema
schema.datePublished = publishDate.toISOString();
schema.description = description; // Pass the description to the schema
schema.imageAlt = 'Image of earth from space';
schema.tags = [
  'International',
  'Social Security',
  'Totalization',
  'Expat Benefits',
];
</script>

<svelte:head>
  <title>{title} | SSA.tools</title>
  <meta name="description" content={description} />
  <link rel="canonical" href={schema.url} />
  {@html schema.render()}
  {@html schema.renderSocialMeta()}
</svelte:head>

<div class="guide-page">
  <h1>{title}</h1>
  <p class="postdate">Published: {publishDate.toLocaleDateString()}</p>

  <figure class="hero-image">
    <img
      src={HeroImage}
      width="512"
      height="512"
      alt="Depiction of the earth with North Amerca in the center"
    />
  </figure>

  <p>
    SSA.tools calculates social security benefits based on a person's earnings
    record, copy/pasted from ssa.gov. In cases of work abroad, this record may
    be insufficient to estimate one's benefit.
  </p>
  <p>
    Since the late 1970's, the United States has established a
    <a href="https://www.ssa.gov/international/agreement_descriptions.html"
      >network of bilateral Social Security agreements</a
    > that coordinate the U.S. Social Security program with the comparable
    programs of other countries.
    International Social Security agreements, often called "Totalization
    agreements", help fill gaps in benefit protection for workers who have
    divided their careers between the United States and another country.
  </p>
  <p>
    The ssa.gov website maintains a full list of all <a
      href="https://www.ssa.gov/international/agreements_overview.html"
      >Totalization Agreements</a
    >.
  </p>
  <InlineCTA type="sponsor" {sponsorCopy} />

  <p>
    These agreements allow earned income <a href="https://www.ssa.gov/international/agreement_descriptions.html"
      >credits to be earned overseas and still count towards</a
    > the
    <a href="https://www.ssa.gov/benefits/retirement/planner/credits.html"
      >minimum of 40 credits</a
    > required for benefits in the US. They also allow for an adjusted benefit
    based on the amount of time employed in each location.
  </p>
  <p>
    This website does not provide a way to directly enter overseas earnings nor
    to compute the related benefits. It should be used as a starting point, but
    not the final answer, in a case of a multinational career.
  </p>

  <h2>Additional Resources</h2>

  <ul>
    <li>
      <a href="https://www.ssa.gov/international/agreements_overview.html"
        >Social Security Administration: U.S. International SSA Agreements</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/international/agreement_descriptions.html"
        >Social Security Administration: International Agreements</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/benefits/retirement/planner/credits.html"
        >Social Security Administration: Social Security Credits and Benefit
        Eligibility</a
      >
    </li>
  </ul>
  <GuideFooter />
</div>
